import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLeague, getAllTimeStandings, getEventStandings, calculateLeagueScores, getCurrentEvent, getLeaguePicks, getLastCompletedEvent, getNextEvent, getEvent, getCompletedEventsWithWinners, getUserBadges } from '../services/api';
import Loading from '../components/Loading';
import UserHoverCard from '../components/UserHoverCard';
import VictoryModal from '../components/VictoryModal';
import NextEventCard from '../components/NextEventCard';
import OctaAvatar from '../components/OctaAvatar';
import { LbRow, LbPodium, avatarBg, ChevronIcon, CheckIcon, TrophyIcon } from '../components/LeaderboardParts';
import '../styles/app/leaderboard.css';
import { HIDE_MONEY } from '../utils/platform';

const LIVE_REFRESH_INTERVAL = 30000; // 30 seconds

function PoolLeaderboard() {
  const { id: poolId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const eventIdParam = searchParams.get('event');
  const { user } = useAuth();
  const historicalStandingsRef = useRef(null);
  const [pool, setPool] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('all-time');
  const [leaderboard, setLeaderboard] = useState([]);
  const [liveStandings, setLiveStandings] = useState([]);
  const [completedFights, setCompletedFights] = useState(0);
  const [totalFights, setTotalFights] = useState(0);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [lastCompletedEvent, setLastCompletedEvent] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [completedEventStandings, setCompletedEventStandings] = useState([]);
  const [completedEventFights, setCompletedEventFights] = useState(0);
  const [requestedEventStandings, setRequestedEventStandings] = useState([]);
  const [requestedEventFights, setRequestedEventFights] = useState(0);
  const [userBadges, setUserBadges] = useState({}); // { userId: [badges] }

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolId, eventIdParam]);

  useEffect(() => {
    if (pool) {
      fetchLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId, pool]);

  // Auto-refresh for live events
  const fetchLiveData = useCallback(async () => {
    if (!currentEvent || currentEvent.status !== 'locked') return;
    try {
      const picksRes = await getLeaguePicks(poolId, currentEvent.id);
      // Build standings from picks data
      const memberStats = {};
      pool?.members?.forEach(m => {
        memberStats[m.id] = {
          user_id: m.id,
          username: m.username,
          display_name: m.display_name,
          correct: 0,
          total: 0,
          points: 0,
          method_bonus: 0
        };
      });

      // Count correct picks and points from the picks data
      // API returns { users: [{id, username, picks: [...]}], fights: { total, completed } }
      const responseData = picksRes.data || {};
      const users = responseData.users || responseData || []; // Handle both new and old format
      const fightsInfo = responseData.fights || {};

      (Array.isArray(users) ? users : []).forEach(userData => {
        // Ensure entry exists even if user isn't in current pool.members (e.g. past event members)
        if (!memberStats[userData.id]) {
          memberStats[userData.id] = {
            user_id: userData.id,
            username: userData.username,
            display_name: userData.display_name,
            correct: 0,
            total: 0,
            points: 0,
            method_bonus: 0
          };
        }
        const userPicks = userData.picks || [];
        userPicks.forEach(pick => {
          if (pick.is_correct) {
            memberStats[userData.id].correct++;
            memberStats[userData.id].points += pick.points_earned || 0;
            memberStats[userData.id].method_bonus += pick.method_bonus || 0;
          }
        });
      });

      // Use fight counts from API (filtered by pool's fight_inclusion setting)
      setCompletedFights(fightsInfo.completed || 0);
      setTotalFights(fightsInfo.total || 0);

      const standings = Object.values(memberStats).sort((a, b) => b.points - a.points);
      setLiveStandings(standings);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch live data:', error);
    }
  }, [poolId, currentEvent, pool?.members]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (currentEvent?.status === 'locked' && autoRefreshEnabled) {
      fetchLiveData();
      const interval = setInterval(fetchLiveData, LIVE_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [currentEvent?.status, autoRefreshEnabled, fetchLiveData]);

  // Scroll to historical standings if hash is present
  useEffect(() => {
    if (!loading && location.hash === '#historical-standings' && historicalStandingsRef.current) {
      setTimeout(() => {
        // Get the element's position and scroll with offset for navbar
        const element = historicalStandingsRef.current;
        const navbarHeight = 80; // Account for fixed navbar
        const bannerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--banner-height') || '0', 10);
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight - bannerHeight - 20; // 20px extra padding

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [loading, location.hash]);

  // Fetch last completed event and next event for victory modal
  useEffect(() => {
    const fetchCompletedEventData = async () => {
      try {
        const [lastCompletedRes, nextEventRes] = await Promise.all([
          getLastCompletedEvent().catch(() => ({ data: null })),
          getNextEvent().catch(() => ({ data: null }))
        ]);
        setNextEvent(nextEventRes.data);

        // If we have a completed event, fetch its standings for this pool
        if (lastCompletedRes.data && pool) {
          const standingsRes = await getLeaguePicks(poolId, lastCompletedRes.data.id).catch(() => ({ data: {} }));
          const responseData = standingsRes.data || {};
          const users = responseData.users || [];
          const fightsInfo = responseData.fights || {};

          // Check if pool actually participated in this event
          const totalPicksForEvent = users.reduce((sum, userData) => {
            return sum + (userData.picks?.length || 0);
          }, 0);

          // Only show results if pool had picks for this event
          if (totalPicksForEvent === 0) {
            // Pool didn't participate, don't set completed event
            setLastCompletedEvent(null);
          } else {
            // Pool participated - set the completed event and check for modal
            setLastCompletedEvent(lastCompletedRes.data);

            // Track completed fights for the completed event
            setCompletedEventFights(fightsInfo.completed || fightsInfo.total || 0);

            // Build standings from picks data
            const memberStats = {};
            pool.members?.forEach(m => {
              memberStats[m.id] = {
                user_id: m.id,
                username: m.username,
                display_name: m.display_name,
                correct: 0,
                points: 0,
                method_bonus: 0
              };
            });

            users.forEach(userData => {
              // Ensure entry exists even if user isn't in current pool.members (e.g. past event members)
              if (!memberStats[userData.id]) {
                memberStats[userData.id] = {
                  user_id: userData.id,
                  username: userData.username,
                  display_name: userData.display_name,
                  correct: 0,
                  points: 0,
                  method_bonus: 0
                };
              }
              const userPicks = userData.picks || [];
              userPicks.forEach(pick => {
                if (pick.is_correct) {
                  memberStats[userData.id].correct++;
                  memberStats[userData.id].points += pick.points_earned || 0;
                  memberStats[userData.id].method_bonus += pick.method_bonus || 0;
                }
              });
            });

            const standings = Object.values(memberStats).sort((a, b) => b.points - a.points);
            setCompletedEventStandings(standings);

            // Show victory modal if not dismissed
            const dismissedKey = `celebration-dismissed-${poolId}-${lastCompletedRes.data.id}`;
            if (!localStorage.getItem(dismissedKey)) {
              setShowVictoryModal(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch completed event data:', error);
      }
    };

    if (pool) {
      fetchCompletedEventData();
    }
  }, [pool, poolId]);

  const handleDismissVictory = () => {
    if (lastCompletedEvent?.id) {
      const dismissedKey = `celebration-dismissed-${poolId}-${lastCompletedEvent.id}`;
      localStorage.setItem(dismissedKey, 'true');
    }
    setShowVictoryModal(false);
  };

  const fetchInitialData = async () => {
    try {
      const [poolRes, currentEventRes] = await Promise.all([
        getLeague(poolId),
        // If event ID is in URL, fetch that specific event, otherwise get current
        eventIdParam ? getEvent(eventIdParam) : getCurrentEvent().catch(() => ({ data: null }))
      ]);
      setPool(poolRes.data);
      setCurrentEvent(currentEventRes.data);

      // Fetch badges for members if this is the global pool
      if (poolRes.data?.is_global && poolRes.data?.members?.length > 0) {
        const badgePromises = poolRes.data.members.map(m =>
          getUserBadges(m.id).then(res => ({ userId: m.id, badges: res.data })).catch(() => ({ userId: m.id, badges: [] }))
        );
        const badgeResults = await Promise.all(badgePromises);
        const badgeMap = {};
        badgeResults.forEach(({ userId, badges }) => {
          if (badges && badges.length > 0) badgeMap[userId] = badges;
        });
        setUserBadges(badgeMap);
      }

      // Fetch only events this pool participated in for the dropdown
      const eventsRes = await getCompletedEventsWithWinners(poolId).catch(() => ({ data: [] }));
      setEvents(eventsRes.data || []);

      // If viewing a specific event from URL, fetch its standings
      if (eventIdParam && poolRes.data) {
        setSelectedEventId(parseInt(eventIdParam));

        // Fetch standings for the requested event using the same method as completedEventStandings
        const standingsRes = await getLeaguePicks(poolId, eventIdParam).catch(() => ({ data: {} }));
        const responseData = standingsRes.data || {};
        const users = responseData.users || [];
        const fightsInfo = responseData.fights || {};

        // Track fight count for this event
        setRequestedEventFights(fightsInfo.completed || fightsInfo.total || 0);

        // Build standings from picks data
        const memberStats = {};
        poolRes.data.members?.forEach(m => {
          memberStats[m.id] = {
            user_id: m.id,
            username: m.username,
            display_name: m.display_name,
            correct: 0,
            points: 0,
            method_bonus: 0
          };
        });

        users.forEach(userData => {
          // Ensure entry exists even if user isn't in current pool.members (e.g. past event members)
          if (!memberStats[userData.id]) {
            memberStats[userData.id] = {
              user_id: userData.id,
              username: userData.username,
              display_name: userData.display_name,
              correct: 0,
              points: 0,
              method_bonus: 0
            };
          }
          const userPicks = userData.picks || [];
          userPicks.forEach(pick => {
            if (pick.is_correct) {
              memberStats[userData.id].correct++;
              memberStats[userData.id].points += pick.points_earned || 0;
              memberStats[userData.id].method_bonus += pick.method_bonus || 0;
            }
          });
        });

        const standings = Object.values(memberStats).sort((a, b) => b.points - a.points);
        setRequestedEventStandings(standings);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      let res;
      if (selectedEventId === 'all-time') {
        res = await getAllTimeStandings(poolId);
      } else {
        res = await getEventStandings(poolId, selectedEventId);
      }
      setLeaderboard(res.data || []);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      setLeaderboard([]);
    }
  };

  const handleCalculateScores = async () => {
    if (selectedEventId === 'all-time') return;
    setCalculating(true);
    try {
      await calculateLeagueScores(poolId, selectedEventId);
      fetchLeaderboard();
    } catch (error) {
      console.error('Failed to calculate scores:', error);
    } finally {
      setCalculating(false);
    }
  };

  if (loading) return <Loading />;

  const isCommissioner = pool?.commissioner_id === user?.id;
  const isGlobal = pool?.is_global === true;
  const paidMembers = pool?.members?.filter(m => m.has_paid).length || 0;
  const totalPot = paidMembers * (pool?.entry_fee || 0);
  const selectedEvent = events.find(e => e.id === parseInt(selectedEventId));

  const payouts = [
    { place: '1st', pct: pool?.payout_first || 60, amount: (totalPot * (pool?.payout_first || 60) / 100).toFixed(2) },
    { place: '2nd', pct: pool?.payout_second || 30, amount: (totalPot * (pool?.payout_second || 30) / 100).toFixed(2) },
    { place: '3rd', pct: pool?.payout_third || 10, amount: (totalPot * (pool?.payout_third || 10) / 100).toFixed(2) },
  ];

  const isLive = currentEvent?.status === 'locked' && currentEvent?.date && new Date(currentEvent.date) <= new Date();
  const membersForDisplay = pool?.members || [];

  // Determine which event to show in the main card:
  // - If there's a locked (live) event, show it
  // - If there's a completed event and current is open/draft, show completed results for 24 hours after event
  // Use completed_at timestamp if available, otherwise fall back to event date
  const completedTimestamp = lastCompletedEvent?.completed_at || lastCompletedEvent?.date;
  const isCompletedWithin24Hours = lastCompletedEvent && completedTimestamp &&
    (new Date().getTime() - new Date(completedTimestamp).getTime()) < 24 * 60 * 60 * 1000;
  const showCompletedResults = lastCompletedEvent && currentEvent?.status !== 'locked' && completedEventStandings.length > 0 && isCompletedWithin24Hours;

  // Check if user has a title shot belt badge
  const hasBeltBadge = (userId) => {
    return userBadges[userId]?.some(b => b.badge_type === 'title_shot_winner');
  };

  const hasWhiteHouseBelt = (userId) => {
    return userBadges[userId]?.some(b => b.badge_type === 'white_house_belt');
  };

  // Get tooltip text for belt badge
  const getBeltTooltip = (userId) => {
    const badges = userBadges[userId]?.filter(b => b.badge_type === 'title_shot_winner') || [];
    if (badges.length === 0) return 'PNP Title Shot Winner';
    if (badges.length === 1) {
      const eventName = badges[0].events?.name;
      return eventName ? `PNP Title Shot Winner — ${eventName}` : 'PNP Title Shot Winner';
    }
    return `PNP Title Shot Winner (${badges.length}x)`;
  };

  // Get rank display text (handles ties)
  const getRankText = (rank) => {
    if (rank === 1) return '1st';
    if (rank === 2) return '2nd';
    if (rank === 3) return '3rd';
    return `${rank}th`;
  };

  // Calculate ranks with tie handling (client-side for live standings)
  const addRanksToStandings = (standings) => {
    if (!standings || standings.length === 0) return [];
    const result = [];
    standings.forEach((entry, index) => {
      if (index === 0) {
        result.push({ ...entry, rank: 1 });
      } else {
        const prev = standings[index - 1];
        const prevRank = result[index - 1].rank;
        // Tie if same points and same correct picks
        if (entry.points === prev.points && entry.correct === prev.correct) {
          result.push({ ...entry, rank: prevRank });
        } else {
          result.push({ ...entry, rank: index + 1 });
        }
      }
    });
    return result;
  };

  // ---- Presentation: which standings the main card shows (same precedence as before) ----
  const standingsSource = eventIdParam && requestedEventStandings.length > 0
    ? 'requested'
    : showCompletedResults && completedEventStandings.length > 0
      ? 'completed'
      : isLive && liveStandings.length > 0
        ? 'live'
        : 'members';
  const rankedStandings = addRanksToStandings(
    // If viewing a specific event via URL param, use those standings
    standingsSource === 'requested'
      ? requestedEventStandings
      : standingsSource === 'completed'
        ? completedEventStandings
        : standingsSource === 'live'
          ? liveStandings
          : membersForDisplay.map(m => ({
              user_id: m.id,
              username: m.username,
              display_name: m.display_name,
              correct: 0,
              total: 0,
              points: 0,
              method_bonus: 0
            }))
  );
  const recordTotal = eventIdParam && requestedEventFights ? requestedEventFights : showCompletedResults ? completedEventFights : completedFights || 0;
  const showMainPodium = rankedStandings.length >= 3 && (rankedStandings[0].points || 0) > 0;
  const showHistoryPodium = selectedEventId === 'all-time' && leaderboard.length >= 3 && !showMainPodium && (leaderboard[0]?.total_points || 0) > 0;
  const showPrizePool = !HIDE_MONEY && !isGlobal && totalPot > 0 && currentEvent;
  const showNextEvent = showCompletedResults && nextEvent;

  const renderBelts = (userId) => (
    <>
      {hasBeltBadge(userId) && (
        <img className="lb-belt" src="/images/title-belt.svg" alt="Title Shot Winner" title={getBeltTooltip(userId)} />
      )}
      {hasWhiteHouseBelt(userId) && (
        <img className="lb-belt" src="/images/white-house-belt.svg" alt="White House Belt" title="White House Belt — UFC Freedom 250 winner" />
      )}
    </>
  );

  const renderName = (entry, isCurrentUser) => (
    <div className="lb-name-line">
      <span className="lb-name">
        <UserHoverCard userId={entry.user_id} username={entry.username}>
          <span>{entry.username}</span>
        </UserHoverCard>
      </span>
      {renderBelts(entry.user_id)}
      {isCurrentUser && <span className="pnp-chip lb-you">YOU</span>}
    </div>
  );

  return (
    <div className="container lb-page">
      <Link to={`/pool/${poolId}`} className="lb-back">
        <ChevronIcon left />
        Back to {pool?.name}
      </Link>

      {/* Event Hero — mirrors the /live page so admins/users see live progress in context */}
      {(showCompletedResults ? lastCompletedEvent : currentEvent) && (() => {
        const headerEvent = showCompletedResults ? lastCompletedEvent : currentEvent;
        const headerCompleted = showCompletedResults ? completedEventFights : completedFights;
        const headerTotal = totalFights || headerCompleted;
        const headerPercent = headerTotal > 0 ? (headerCompleted / headerTotal) * 100 : 0;
        const showProgress = !showCompletedResults && headerTotal > 0;

        return (
          <section className={`lb-hero pnp-rise${isLive ? ' is-live' : showCompletedResults ? ' is-final' : ''}`}>
            {(isLive || showCompletedResults) && <div className="pnp-glow lb-hero-glow" aria-hidden="true" />}
            {isLive && <span className="pnp-claw" aria-hidden="true" />}
            <div className="lb-hero-inner">
              <div className="lb-hero-main">
                <div className="lb-hero-status">
                  {isLive ? (
                    <span className="pnp-live-pill lb-live-pill-lg"><span className="pnp-live-dot" />Live Now</span>
                  ) : showCompletedResults ? (
                    <span className="pnp-chip pnp-chip-green"><CheckIcon />Event Completed</span>
                  ) : (
                    <span className="pnp-eyebrow pnp-eyebrow-mute">Current Event</span>
                  )}
                </div>
                <h1 className="pnp-title lb-hero-name">{headerEvent.name}</h1>
                <p className="lb-hero-date">
                  {new Date(headerEvent.date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
              {showProgress ? (
                <div className="lb-hero-score">
                  <span className="lb-hero-score-label">Fights</span>
                  <div className="lb-hero-score-value">
                    {headerCompleted}<small>/{headerTotal}</small>
                  </div>
                </div>
              ) : showCompletedResults ? (
                <div className="lb-hero-score">
                  <span className="lb-hero-score-label">Final</span>
                  <span className="lb-hero-trophy"><TrophyIcon /></span>
                </div>
              ) : null}
            </div>
            {showProgress && headerTotal <= 30 ? (
              <div className="lb-pips" aria-label={`${headerCompleted} of ${headerTotal} fights completed`}>
                {Array.from({ length: headerTotal }).map((_, i) => (
                  <span key={i} className={`lb-pip${i < headerCompleted ? ' is-done' : isLive && i === headerCompleted ? ' is-next' : ''}`} />
                ))}
              </div>
            ) : (showProgress || showCompletedResults) ? (
              <div className={`lb-bar${showCompletedResults ? ' is-final' : ''}`}>
                <span style={{ width: showCompletedResults ? '100%' : `${headerPercent}%` }} />
              </div>
            ) : null}
          </section>
        );
      })()}

      {/* Next Event Card (when viewing completed results) + Prize Pool (not for global pool) */}
      {(showNextEvent || showPrizePool) && (
        <div className={`lb-extras${showNextEvent && showPrizePool ? ' has-two' : ''}`}>
          {showNextEvent && <NextEventCard event={nextEvent} />}
          {showPrizePool && (
            <div className="card lb-prize">
              <div className="lb-prize-head">
                <h3 className="lb-prize-title">Prize Pool</h3>
                <span className="lb-prize-total">${totalPot}</span>
              </div>
              <div className="lb-prize-grid">
                {payouts.map((p, i) => (
                  <div key={i} className={`pnp-tile lb-prize-tile${i === 0 ? ' is-first' : ''}`}>
                    <span className="lb-prize-place">{p.place} ({p.pct}%)</span>
                    <span className="lb-prize-amount">${p.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live Event Banner */}
      {isLive && (
        <div className="lb-meta">
          {lastUpdated && (
            <span className="lb-meta-item">
              <span className="pnp-live-dot" />
              Last updated: <span className="pnp-num">{lastUpdated.toLocaleTimeString()}</span>
            </span>
          )}
          {autoRefreshEnabled && <span className="lb-meta-item">Auto-refreshes every 30 seconds</span>}
        </div>
      )}

      {/* Standings Card - Shows during live event or completed event */}
      {(isLive || showCompletedResults || membersForDisplay.length > 0) && (
        <section className="card lb-card pnp-rise" style={{ '--i': 1 }}>
          <div className="lb-card-head">
            <div className="lb-card-titles">
              <h2 className="lb-card-title">
                {pool?.name}
                {isLive && (
                  <span className="pnp-live-pill"><span className="pnp-live-dot" />LIVE</span>
                )}
                {showCompletedResults && !isLive && (
                  <span className="pnp-chip pnp-chip-green">FINAL RESULTS</span>
                )}
              </h2>
              <p className="lb-card-sub">
                <span className="pnp-num">{membersForDisplay.length}</span> member{membersForDisplay.length !== 1 ? 's' : ''} competing
              </p>
            </div>
            <div className="lb-card-actions">
              <Link to={`/pool/${poolId}/picks${eventIdParam ? `?event=${eventIdParam}` : showCompletedResults && lastCompletedEvent ? `?event=${lastCompletedEvent.id}` : ''}`} className="btn btn-secondary">
                View Picks
              </Link>
              <Link to={`/pool/${poolId}`} className="btn btn-primary">
                Pool Details
              </Link>
            </div>
          </div>

          <div className="lb-card-body">
            <div className={`lb-split${showMainPodium ? ' has-podium' : ''}`}>
              {showMainPodium && (
                <LbPodium entries={rankedStandings} getPts={(e) => e.points || 0} userId={user?.id} />
              )}

              {/* Standings rows */}
              <div className="lb-rows">
                <div className="lb-row lb-row-head">
                  <span>#</span>
                  <span className="lb-h-avatar" aria-hidden="true" />
                  <span className="lb-h-player">Player</span>
                  <span className="lb-h-stat">Record</span>
                  <span className="lb-h-stat">Method</span>
                  <span className="lb-h-pts">Pts</span>
                  <span>Picks</span>
                </div>
                {rankedStandings.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  const rank = entry.rank || 1;

                  return (
                    <LbRow key={entry.user_id} rank={rank} scope={standingsSource === 'live' ? 'live' : null} index={index} isMe={isCurrentUser} plain={!((rankedStandings[0]?.points || 0) > 0)}>
                      <div className="lb-avatar">
                        <OctaAvatar name={entry.username} size={34} bg={avatarBg(entry.user_id, isCurrentUser)} />
                      </div>
                      <div className="lb-player">
                        {renderName(entry, isCurrentUser)}
                        <div className="lb-sub lb-sub-mobile">
                          <span><span className="pnp-num">{entry.correct}/{recordTotal}</span> record</span>
                          <span className="is-method"><span className="pnp-num">+{entry.method_bonus || 0}</span> method</span>
                        </div>
                      </div>
                      <span className="lb-cell">{entry.correct}/{recordTotal}</span>
                      <span className="lb-cell is-method">+{entry.method_bonus || 0}</span>
                      <span className="lb-pts">{entry.points || 0}</span>
                      <Link
                        className="lb-view"
                        aria-label={`View ${entry.username}'s picks`}
                        to={`/pool/${poolId}/picks?event=${showCompletedResults ? lastCompletedEvent?.id : currentEvent?.id}&user=${entry.user_id}`}
                      >
                        <span><em className="lb-view-text">View</em><ChevronIcon /></span>
                      </Link>
                    </LbRow>
                  );
                })}
              </div>
            </div>
          </div>

          {isLive && (
            <div className="lb-card-foot">
              <label className="lb-switch">
                <input
                  type="checkbox"
                  className="pnp-switch"
                  checked={autoRefreshEnabled}
                  onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                />
                Auto-refresh
              </label>
            </div>
          )}
        </section>
      )}

      <section
        ref={historicalStandingsRef}
        id="historical-standings"
        className="lb-history"
      >
        <div className="lb-history-grid">
          <div className="lb-history-side">
            <p className="pnp-eyebrow pnp-eyebrow-gold">Record Book</p>
            <h1 className="pnp-title">Historical Standings</h1>
            <p className="pnp-page-sub">
              {selectedEventId === 'all-time' ? 'All-Time Standings' : selectedEvent?.name || 'Select an event'}
            </p>
            <div className="lb-history-controls">
              <select
                className="form-select"
                aria-label="Standings view"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
              >
                <option value="all-time">All-Time Standings</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
              {isCommissioner && !isGlobal && selectedEventId !== 'all-time' && (
                <button
                  className="btn btn-secondary"
                  onClick={handleCalculateScores}
                  disabled={calculating}
                >
                  {calculating ? 'Calculating...' : 'Recalculate'}
                </button>
              )}
            </div>
            {showHistoryPodium && (
              <LbPodium entries={leaderboard} getPts={(e) => e.total_points || 0} userId={user?.id} />
            )}
          </div>

          <div className="lb-history-main">
            {leaderboard.length === 0 ? (
              <div className="pnp-empty">
                <div className="pnp-empty-icon"><TrophyIcon /></div>
                <h3 className="pnp-empty-title">No Data Yet</h3>
                <p>
                  {selectedEventId === 'all-time'
                    ? 'Complete an event to see all-time standings.'
                    : 'No picks have been scored for this event yet.'}
                </p>
                {selectedEventId !== 'all-time' && (
                  <div className="pnp-empty-actions">
                    <Link to={`/pool/${poolId}/picks`} className="btn btn-primary">
                      Make Picks
                    </Link>
                  </div>
                )}
              </div>
            ) : selectedEventId === 'all-time' ? (
              /* All-Time Standings - Card Layout */
              <div className="lb-rows is-alltime">
                {leaderboard.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  const rank = entry.rank || (index + 1);

                  return (
                    <LbRow key={entry.user_id} rank={rank} index={index} isMe={isCurrentUser}>
                      <div className="lb-avatar">
                        <OctaAvatar name={entry.username} size={40} bg={avatarBg(entry.user_id, isCurrentUser)} />
                      </div>
                      <div className="lb-player">
                        {renderName(entry, isCurrentUser)}
                        <div className="lb-sub">
                          <span><span className="pnp-num">{entry.correct_picks || 0}/{entry.total_picks || 0}</span> picks · <span className="pnp-num">{entry.pick_accuracy || 0}%</span></span>
                          {entry.total_method_picks > 0 && (
                            <span><span className="pnp-num">{entry.correct_methods || 0}/{entry.total_method_picks || 0}</span> methods · <span className="pnp-num">{entry.method_accuracy || 0}%</span></span>
                          )}
                          {entry.wins > 0 && (
                            <span className="is-wins">{entry.wins} win{entry.wins !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                      <div className="lb-pts-stack">
                        <span className="lb-pts">{entry.total_points || 0}</span>
                        <span className="lb-pts-label">pts</span>
                      </div>
                    </LbRow>
                  );
                })}
              </div>
            ) : (
              /* Event Standings */
              <div className="card lb-event-card">
                <div className="lb-rows is-event">
                  <div className="lb-row lb-row-head">
                    <span>#</span>
                    <span className="lb-h-player">Player</span>
                    <span className="lb-h-pts">Pts</span>
                  </div>
                  {leaderboard.map((entry, index) => {
                    const isCurrentUser = entry.user_id === user?.id;
                    const rank = entry.rank || (index + 1);

                    return (
                      <LbRow key={entry.user_id} rank={rank} rankLabel={getRankText(rank)} index={index} isMe={isCurrentUser}>
                        <div className="lb-player">
                          {renderName(entry, isCurrentUser)}
                        </div>
                        <span className="lb-pts">{entry.total_points || 0}</span>
                      </LbRow>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Victory Modal */}
      {showVictoryModal && lastCompletedEvent && (
        <VictoryModal
          event={lastCompletedEvent}
          standings={addRanksToStandings(completedEventStandings)}
          onClose={handleDismissVictory}
          poolName={pool?.name}
          entryFee={pool?.entry_fee || 0}
          memberCount={paidMembers}
          payoutFirst={pool?.payout_first || 60}
          payoutSecond={pool?.payout_second || 30}
          payoutThird={pool?.payout_third || 10}
          nextEvent={nextEvent}
          isGlobal={isGlobal}
        />
      )}
    </div>
  );
}

export default PoolLeaderboard;
